import React, { useState, useRef } from 'react';
import { Upload, Download, X, FileText, CheckCircle, AlertCircle, Loader } from 'lucide-react';
import { investorContactAPI } from '../../services/api';

const InvestorBulkImportModal = ({ isOpen, onClose, onSuccess }) => {
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [dragOver, setDragOver] = useState(false);

  if (!isOpen) return null;

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (!selectedFile.name.endsWith('.csv')) {
        alert('Please select a CSV file');
        return;
      }
      setFile(selectedFile);
      setResult(null);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      if (!droppedFile.name.endsWith('.csv')) {
        alert('Please select a CSV file');
        return;
      }
      setFile(droppedFile);
      setResult(null);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await investorContactAPI.downloadTemplate();
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'investor_contacts_template.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download template:', err);
      alert('Failed to download template');
    }
  };

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    setResult(null);
    try {
      const response = await investorContactAPI.bulkImport(file);
      setResult(response.data);
      if (response.data.created > 0) {
        onSuccess?.();
      }
    } catch (err) {
      console.error('Bulk import failed:', err);
      const detail = err.response?.data?.detail;
      setResult({
        created: 0,
        skipped: 0,
        message: detail || 'Import failed. Please check your CSV file format.',
        errors: []
      });
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setResult(null);
    setUploading(false);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="bulk-import-modal">
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center p-5 border-b">
          <h2 className="text-lg font-bold text-gray-900">Bulk Import Investor Contacts</h2>
          <button onClick={handleClose} className="p-1 hover:bg-gray-100 rounded" data-testid="close-bulk-import-modal">
            <X size={20} />
          </button>
        </div>

        <div className="p-5 space-y-5">
          {/* Download Template */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800 mb-2">
              Download the CSV template to see the required format. Fill it with your investor data and upload.
            </p>
            <button
              onClick={handleDownloadTemplate}
              className="inline-flex items-center text-sm text-blue-700 font-medium hover:text-blue-900"
              data-testid="download-template-btn"
            >
              <Download size={16} className="mr-1" />
              Download CSV Template
            </button>
          </div>

          {/* File Upload Area */}
          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
              dragOver ? 'border-blue-400 bg-blue-50' : file ? 'border-green-400 bg-green-50' : 'border-gray-300 hover:border-gray-400'
            }`}
            data-testid="file-drop-zone"
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleFileSelect}
              className="hidden"
              data-testid="file-input"
            />
            {file ? (
              <div className="flex items-center justify-center gap-2">
                <FileText size={24} className="text-green-600" />
                <div>
                  <p className="font-medium text-gray-900">{file.name}</p>
                  <p className="text-sm text-gray-500">{(file.size / 1024).toFixed(1)} KB</p>
                </div>
                <button
                  onClick={(e) => { e.stopPropagation(); setFile(null); setResult(null); }}
                  className="ml-2 p-1 text-gray-400 hover:text-red-500"
                >
                  <X size={16} />
                </button>
              </div>
            ) : (
              <>
                <Upload size={32} className="mx-auto text-gray-400 mb-2" />
                <p className="text-gray-600 font-medium">Drop CSV file here or click to browse</p>
                <p className="text-sm text-gray-400 mt-1">Only .csv files are supported</p>
              </>
            )}
          </div>

          {/* Expected Columns */}
          <div className="text-xs text-gray-500">
            <p className="font-medium mb-1">Required columns: First Name, Last Name, Email</p>
            <p>Optional: Salutation, Middle Name, Mobile, Telephone, WhatsApp Opt-out, Distributor Name, Investor Lead, Brief Profile, Educational Background, Coverage Urgency, Touch Base Required</p>
          </div>

          {/* Upload Result */}
          {result && (
            <div className={`rounded-lg p-4 border ${result.created > 0 ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`} data-testid="import-result">
              <div className="flex items-start gap-2">
                {result.created > 0 ? (
                  <CheckCircle size={20} className="text-green-600 mt-0.5" />
                ) : (
                  <AlertCircle size={20} className="text-red-600 mt-0.5" />
                )}
                <div>
                  <p className={`font-medium ${result.created > 0 ? 'text-green-800' : 'text-red-800'}`}>
                    {result.message}
                  </p>
                  {result.errors && result.errors.length > 0 && (
                    <div className="mt-2 space-y-1">
                      <p className="text-sm font-medium text-red-700">Errors:</p>
                      {result.errors.map((err, i) => (
                        <p key={i} className="text-xs text-red-600">
                          Row {err.row}: {err.error}
                        </p>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-5 border-t">
          <button
            onClick={handleClose}
            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 text-sm"
          >
            {result?.created > 0 ? 'Done' : 'Cancel'}
          </button>
          <button
            onClick={handleUpload}
            disabled={!file || uploading}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm inline-flex items-center"
            data-testid="upload-btn"
          >
            {uploading ? (
              <>
                <Loader size={16} className="mr-2 animate-spin" />
                Importing...
              </>
            ) : (
              <>
                <Upload size={16} className="mr-2" />
                Import Contacts
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default InvestorBulkImportModal;
